"use client"

import React, { useState, useEffect } from "react"

import { Sidebar } from "../layout/sidebar"

import { Header } from "../layout/header"

import { Footer } from "../layout/footer"

import { useLocation } from "react-router-dom"

import "./globals.css"
import { Toaster } from "../ui/toaster"

interface ClientLayoutProps {

  children: React.ReactNode

}

export default function ClientLayout({ children }: ClientLayoutProps) {

  const [sidebarOpen, setSidebarOpen] = useState(false)

  const location = useLocation()

  const [roleId, setRoleId] = useState<string | null>(null);
  const [hasDashboardRoles, setHasDashboardRoles] = useState(true);

  useEffect(() => {
    const storedRoleId = localStorage.getItem("roleId");
    if (storedRoleId) setRoleId(storedRoleId);
    
    // Check if user has any dashboard roles (excluding admin, secondary approver, coowner)
    const storedRoles = JSON.parse(localStorage.getItem("roles") || "[]") as number[];
    const roleMap: { [key: number]: string } = {
      1: "hr",
      2: "manager",
      3: "employee",
      // Contractor - gets the employee dashboard. Without an entry here the
      // layout treats the user as having no dashboard role and hides the sidebar
      // and header entirely.
      10: "employee",
    };
    
    const dashboardRoles = storedRoles.filter((r) => r !== 6 && r !== 7 && r !== 4);
    const mappedRoles = dashboardRoles
      .map((r) => roleMap[r])
      .filter(Boolean)
      .filter((role) => role !== "admin");
    
    setHasDashboardRoles(mappedRoles.length > 0);
  }, []);

  // detect login page (or any auth-only pages)
  const isLoginPage = location.pathname === "/"

  const handleToggleSidebar = () => setSidebarOpen(prev => !prev)

  // If on login page, render children only
  if (isLoginPage) return <>{children}</>

  // If user has no dashboard roles and is on dashboard, render without sidebar/header
  // The dashboard page itself will handle showing the no-role page
  const isDashboardPage = location.pathname === "/dashboard"
  const shouldHideLayout = isDashboardPage && !hasDashboardRoles

  if (shouldHideLayout) {
    return (
      <>
        {children}
        <Toaster />
      </>
    )
  }

  // For all other pages, render layout with sidebar/header/footer
  return (
    <div

      className={`min-h-screen flex flex-col transition-colors duration-300 bg-gray-50`}
    >
      <Header onSidebarToggle={handleToggleSidebar} />
      <Sidebar isOpen={sidebarOpen} onToggle={handleToggleSidebar} roleId={roleId || "employee"} />
     
      <main

        className={`flex-1 pt-16 transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : ""

          } overflow-x-hidden`}
      >
        <div className="min-h-full flex flex-col w-full max-w-full">{children}</div>
      </main>
      <Footer/>
      <Toaster />
    </div>

  )

}